/**
 * Holds the exam access token issued by the PHP portal.
 *
 * Token formats:
 *   legacy (v1): base64(JSON)                       — unsigned
 *   signed (v2): base64(JSON) + '.' + hex(HMAC-SHA256)
 * base64 never contains '.', so splitting on '.' is safe for both formats.
 *
 * Delivery: the portal launches /exam|staff/<room>#token=... (a fragment is never sent to servers or in
 * Referer headers). On load the token is moved into sessionStorage (this tab only, bound to the launch
 * path so reloads keep working) and removed from the address bar. ?token=... is still read for old links.
 */
const STORAGE_KEY = 'exam_token';
let currentToken = null;

function readSession() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
    return saved && saved.path === window.location.pathname ? saved.token : null;
  } catch (_) {
    return null;
  }
}

function writeSession(token) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path: window.location.pathname, token }));
  } catch (_) {
    // storage disabled: the in-memory copy still serves this page load
  }
}

/** Moves a launch token from the URL into tab storage and strips it from the address bar. Runs once on import. */
function bootstrapFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const search = new URLSearchParams(window.location.search);
    const fromUrl = hash.get('token') || search.get('token');
    if (!fromUrl) {
      currentToken = readSession();
      return;
    }
    currentToken = fromUrl;
    writeSession(fromUrl);
    hash.delete('token');
    search.delete('token');
    const query = search.toString();
    const fragment = hash.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${fragment ? `#${fragment}` : ''}`);
  } catch (_) {
    // malformed URL: leave as-is
  }
}
bootstrapFromUrl();

export function setExamToken(token) {
  currentToken = token || null;
}

export function getExamToken() {
  if (!currentToken && typeof window !== 'undefined') currentToken = readSession();
  return currentToken;
}

export function decodeExamToken(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    return JSON.parse(atob(token.split('.')[0]));
  } catch (_) {
    return null;
  }
}

export function authHeaders() {
  const token = getExamToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
